import React, { useState, useRef, useEffect, useCallback } from 'react';
import { usePartnrStore, PartnrChat, ChatMessage } from '@/stores/partnrStore';
import { useCanvasStore, Item } from '@/stores/canvasStore';
import { X, Sparkles, Loader2, Trash2, Plus, ChevronDown, Copy, Check, ArrowUp, LayoutGrid, Layers, Brain, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { markdownToHtml } from '@/lib/markdownUtils';
import { calculatePlacement } from '@/lib/partnrPlacementUtils';

interface PartnrChatWindowProps {
  chat: PartnrChat;
}

const SUGGESTED_PROMPTS = [
  "Summarize these items",
  "Find connections between these",
  "Help me expand on this",
  "Turn this into an action plan",
];

export function PartnrChatWindow({ chat }: PartnrChatWindowProps) {
  const [inputValue, setInputValue] = useState('');
  const [showContext, setShowContext] = useState(false);
  const [useBoardContext, setUseBoardContext] = useState(true);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);
  const [panelWidth, setPanelWidth] = useState(340);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const MIN_WIDTH = 300;
  const MAX_WIDTH = 600;

  const { 
    closeChat, 
    sendMessage, 
    streamingChatId,
    isStreaming,
    deleteChat,
    setPartnrMode,
    searchEscalationContext,
  } = usePartnrStore();

  const { items, currentBoardId, createItem, panX, panY, zoom } = useCanvasStore();

  const isCurrentlyStreaming = streamingChatId === chat.id && isStreaming;
  const referencedItems = items.filter(item => chat.referenced_item_ids.includes(item.id));

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.messages]);

  // Resize handler
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = panelWidth;
    
    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
      setPanelWidth(newWidth);
    };
    
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [panelWidth]);

  const handleSend = async () => {
    if (!inputValue.trim() || isCurrentlyStreaming) return;
    const message = inputValue;
    setInputValue('');
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    // Set the board context flag before sending
    usePartnrStore.setState({ useBoardContextInNextMessage: useBoardContext });
    await sendMessage(chat.id, message);
  };

  const handleSuggestedPrompt = async (prompt: string) => {
    if (isCurrentlyStreaming) return;
    await sendMessage(chat.id, prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Stop propagation to prevent canvas shortcuts
    e.stopPropagation();
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAddToCanvas = async (content: string) => {
    if (!currentBoardId) {
      toast.error('Please navigate to a board first');
      return;
    }

    // Convert markdown to HTML for proper rendering
    const htmlContent = markdownToHtml(content);

    let posX: number;
    let posY: number;
    const resultWidth = 300;
    const resultHeight = 200;

    if (referencedItems.length > 0) {
      // Smart placement: near the referenced items
      const placement = calculatePlacement(
        referencedItems,
        resultWidth,
        resultHeight,
        items,
      );
      posX = placement.x;
      posY = placement.y;
    } else {
      // Fallback: viewport center
      const viewportCenterX = (window.innerWidth / 2) / zoom - panX;
      const viewportCenterY = (window.innerHeight / 2) / zoom - panY;
      posX = Math.round(viewportCenterX / 20) * 20;
      posY = Math.round(viewportCenterY / 20) * 20;
    }

    const result = await createItem({
      board_id: currentBoardId,
      item_type: 'text_card',
      position_x: posX,
      position_y: posY,
      width: resultWidth,
      height: resultHeight,
      content: { body: htmlContent, partnr_generated: true },
    });

    if (result) {
      toast.success('Added to canvas');
      
      // Record connection for visual lines
      const { setCanvasActionConnection } = usePartnrStore.getState();
      if (referencedItems.length > 0) {
        setCanvasActionConnection({
          sourceItemIds: referencedItems.map(i => i.id),
          resultItemId: result.id,
        });
      }
    }
  };

  const handleAddSplitToCanvas = async (content: string) => {
    if (!currentBoardId) {
      toast.error('Please navigate to a board first');
      return;
    }

    // Intelligent content splitting:
    // 1. Split by ## or ### markdown headers
    // 2. If no headers, split by numbered lists (1. 2. 3.)
    // 3. If sections are too long, they stay as single cards
    // 4. Minimum 2 sections to split, otherwise add as single card

    let sections: { title: string; body: string }[] = [];

    // Try splitting by headers first
    const headerSplit = content.split(/^(?=#{1,3}\s)/m).filter(s => s.trim().length > 0);

    if (headerSplit.length >= 2) {
      sections = headerSplit.map(section => {
        const lines = section.trim().split('\n');
        const firstLine = lines[0].replace(/^#+\s*/, '').trim();
        const rest = lines.slice(1).join('\n').trim();
        return { title: firstLine, body: rest || firstLine };
      });
    } else {
      // Try splitting by numbered items or bold markers
      const listSplit = content.split(/(?=^\d+\.\s|\*\*[^*]+\*\*:)/m).filter(s => s.trim().length > 20);

      if (listSplit.length >= 2) {
        sections = listSplit.map((section, i) => {
          const trimmed = section.trim();
          // Extract title from first bold text or first line
          const boldMatch = trimmed.match(/\*\*([^*]+)\*\*/);
          const title = boldMatch ? boldMatch[1] : `Point ${i + 1}`;
          return { title, body: trimmed };
        });
      }
    }

    if (sections.length < 2) {
      // Not splittable — add as single card
      await handleAddToCanvas(content);
      return;
    }

    // Limit to 8 cards max
    sections = sections.slice(0, 8);

    // Calculate placement — arrange cards in a grid below referenced items
    const startX = referencedItems.length > 0
      ? Math.min(...referencedItems.map(i => i.position_x))
      : (window.innerWidth / 2) / zoom - panX - (sections.length * 75);

    const startY = referencedItems.length > 0
      ? Math.max(...referencedItems.map(i => i.position_y + i.height)) + 60
      : (window.innerHeight / 2) / zoom - panY + 100;

    const cardWidth = 240;
    const cardHeight = 180;
    const gap = 20;

    const createdIds: string[] = [];

    for (let i = 0; i < sections.length; i++) {
      const htmlContent = markdownToHtml(sections[i].body);
      const col = i % 4;
      const row = Math.floor(i / 4);

      const result = await createItem({
        board_id: currentBoardId,
        item_type: 'text_card',
        position_x: Math.round((startX + col * (cardWidth + gap)) / 20) * 20,
        position_y: Math.round((startY + row * (cardHeight + gap + 20)) / 20) * 20,
        width: cardWidth,
        height: cardHeight,
        content: {
          title: sections[i].title,
          body: htmlContent,
          partnr_generated: true,  // Mark as AI-generated for styling
        },
      });

      if (result) createdIds.push(result.id);
    }

    toast.success(`Added ${createdIds.length} cards to canvas`);

    // Show connection lines from source items to first result
    if (referencedItems.length > 0 && createdIds.length > 0) {
      const { setCanvasActionConnection } = usePartnrStore.getState();
      setCanvasActionConnection({
        sourceItemIds: referencedItems.map(i => i.id),
        resultItemId: createdIds[0],
      });
    }
  };

  const handleClose = () => {
    closeChat(chat.id);
    setPartnrMode(false);
  };

  const handleDeleteChat = () => {
    deleteChat(chat.id);
  };

  // Handle copying message content
  const handleCopyMessage = async (content: string, index: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const getItemPreview = (item: Item): string => {
    const content = item.content as Record<string, unknown>;
    switch (item.item_type) {
      case 'sticky':
        return (content.text as string)?.substring(0, 30) || 'Sticky Note';
      case 'text_card':
        return (content.body as string)?.substring(0, 30) || (content.title as string)?.substring(0, 30) || 'Text Card';
      case 'document':
        return (content.title as string)?.substring(0, 30) || 'Document';
      case 'image':
        return (content.alt as string)?.substring(0, 30) || 'Image';
      case 'file':
        return (content.filename as string)?.substring(0, 30) || 'File';
      case 'link':
        return (content.title as string)?.substring(0, 30) || 'Link';
      default:
        return 'Item';
    }
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.role === 'user';
    const isLastAssistantMessage = !isUser && index === chat.messages.length - 1;
    const isMessageStreaming = isCurrentlyStreaming && !isUser && index === chat.messages.length - 1;
    const isCopied = copiedMessageIndex === index;
    
    if (isUser) {
      return (
        <div key={index} className="flex justify-end mb-3 group/message">
          <div className="max-w-[80%] px-3.5 py-2.5 rounded-2xl rounded-br-sm bg-gradient-to-br from-violet-600 to-purple-600 text-white text-[13px] leading-relaxed relative">
            <div className="whitespace-pre-wrap">{message.content}</div>
            {/* Copy button */}
            <button
              onClick={() => handleCopyMessage(message.content, index)}
              className="absolute -bottom-1 right-1 p-1 rounded opacity-0 group-hover/message:opacity-100 transition-opacity hover:bg-white/20 text-white"
              title="Copy message"
            >
              {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={index} className="flex gap-2.5 mb-3 group/message">
        <div className="w-5 h-5 rounded-md bg-partnr flex items-center justify-center shrink-0 mt-0.5">
          <Sparkles className="h-3 w-3 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] leading-relaxed text-foreground whitespace-pre-wrap prose prose-sm dark:prose-invert max-w-none relative [&_*]:text-inherit [&_strong]:font-semibold [&_code]:text-[hsl(var(--foreground))] [&_code]:bg-muted">
            {isMessageStreaming ? (
              <span className="streaming-text">{message.content}</span>
            ) : (
              renderMarkdown(message.content)
            )}
            {/* Copy button */}
            <button
              onClick={() => handleCopyMessage(message.content, index)}
              className="absolute -bottom-1 right-1 p-1 rounded opacity-0 group-hover/message:opacity-100 transition-opacity hover:bg-foreground/10 text-muted-foreground hover:text-foreground"
              title="Copy message"
            >
              {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            </button>
          </div>
          {/* Add to Canvas buttons for assistant messages */}
          {isLastAssistantMessage && !isCurrentlyStreaming && message.content.length > 20 && (
            <div className="flex items-center gap-1.5 mt-2">
              <button
                onClick={() => handleAddToCanvas(message.content)}
                className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add as card
              </button>
              <button
                onClick={() => handleAddSplitToCanvas(message.content)}
                className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-partnr/10 hover:bg-partnr/20 text-partnr transition-colors"
              >
                <LayoutGrid className="w-3 h-3" />
                Split into cards
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Enhanced markdown renderer
  const renderMarkdown = (content: string) => {
    // Handle headers
    let formatted = content.replace(/^### (.*$)/gm, '<h3 class="text-sm font-semibold mt-2 mb-1">$1</h3>');
    formatted = formatted.replace(/^## (.*$)/gm, '<h2 class="text-base font-semibold mt-2 mb-1">$1</h2>');
    formatted = formatted.replace(/^# (.*$)/gm, '<h1 class="text-lg font-bold mt-2 mb-1">$1</h1>');
    // Handle bold
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Handle italic
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // Handle inline code
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-xs font-mono">$1</code>');
    // Handle bullet points
    formatted = formatted.replace(/^- (.*$)/gm, '<li class="ml-4 list-disc">$1</li>');
    formatted = formatted.replace(/^• (.*$)/gm, '<li class="ml-4 list-disc">$1</li>');
    // Handle numbered lists
    formatted = formatted.replace(/^\d+\. (.*$)/gm, '<li class="ml-4 list-decimal">$1</li>');
    
    return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  // Stop wheel events from propagating to canvas
  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  // Handle input focus
  const handleInputFocus = (e: React.FocusEvent) => {
    e.stopPropagation();
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    inputRef.current?.focus();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 20, scale: 0.98 }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        "fixed z-[60] flex flex-col partnr-chat-window",
        "bg-background/95 backdrop-blur-xl border border-border/50",
        "right-4 top-24 bottom-36 rounded-xl shadow-2xl"
      )}
      style={{ width: panelWidth }}
      onWheel={handleWheel}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-partnr/20 active:bg-partnr/30 transition-colors z-10"
        onMouseDown={handleResizeStart}
      >
        <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full bg-border opacity-0 hover:opacity-100 transition-opacity" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 shrink-0 bg-background/80 backdrop-blur-xl bg-gradient-to-r from-violet-600/10 to-purple-600/10 rounded-t-xl">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-partnr flex items-center justify-center shrink-0">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="font-semibold text-sm text-foreground">Partnr</span>
          {referencedItems.length > 0 && (
            <span className="text-xs text-muted-foreground">
              · {referencedItems.length} item{referencedItems.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-0.5">
          {/* Delete */}
          <button
            onClick={handleDeleteChat}
            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Delete chat"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          
          {/* Close */}
          <button
            onClick={handleClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Context Indicator */}
      {referencedItems.length > 0 && (
        <div className="px-4 py-2 border-b border-border/50 shrink-0">
          <button 
            onClick={() => setShowContext(!showContext)} 
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
          >
            <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", showContext && "rotate-180")} />
            <span>Referencing {referencedItems.length} item{referencedItems.length > 1 ? 's' : ''}</span>
          </button>
          {showContext && (
            <div className="mt-2 space-y-1.5 pb-1">
              {referencedItems.map((item, index) => (
                <div key={item.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="w-4 h-4 rounded-full bg-partnr-static text-white text-[10px] flex items-center justify-center font-bold shrink-0">
                    {index + 1}
                  </span>
                  <span className="truncate">{getItemPreview(item)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Board context indicator - toggleable */}
      {(() => {
        const { boardProfile } = useCanvasStore();
        if (!boardProfile?.semantic_summary) return null;
        
        return (
          <div className="px-4 py-2 border-b border-border/50 shrink-0 bg-muted/20">
            <button
              onClick={() => setUseBoardContext(!useBoardContext)}
              className="flex items-center justify-between w-full group"
            >
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Brain className={cn("h-3 w-3", useBoardContext ? "text-partnr" : "text-muted-foreground/50")} />
                <span className={cn("font-medium", useBoardContext ? "text-foreground/80" : "text-muted-foreground/60")}>
                  Board context
                </span>
              </div>
              <div className={cn(
                "w-7 h-4 rounded-full transition-colors flex items-center px-0.5",
                useBoardContext ? "bg-partnr" : "bg-muted-foreground/30"
              )}>
                <div className={cn(
                  "w-3 h-3 rounded-full bg-white transition-transform",
                  useBoardContext ? "translate-x-3" : "translate-x-0"
                )} />
              </div>
            </button>
            {useBoardContext && (
              <>
                <p className="text-[11px] text-muted-foreground/80 mt-1 line-clamp-2">
                  {boardProfile.semantic_summary}
                </p>
                {boardProfile.key_topics.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {boardProfile.key_topics.slice(0, 4).map(topic => (
                      <span
                        key={topic}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-partnr/10 text-partnr"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Messages */}
      <ScrollArea 
        className="flex-1 min-h-0"
        style={{ overscrollBehavior: 'contain' }}
      >
        <div className="p-4 space-y-1">
          {chat.messages.map((message, index) => renderMessage(message, index))}
          
          {/* Typing Indicator */}
          {isCurrentlyStreaming && chat.messages[chat.messages.length - 1]?.role === 'user' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-2.5 mb-3"
            >
              <div className="w-5 h-5 rounded-md bg-partnr flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="h-3 w-3 text-white" />
              </div>
              <div className="flex items-center gap-1.5 py-2 px-3 rounded-xl bg-muted/50">
                <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.8, repeat: Infinity, delay: 0 }} className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.8, repeat: Infinity, delay: 0.15 }} className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.8, repeat: Infinity, delay: 0.3 }} className="w-1.5 h-1.5 rounded-full bg-violet-500" />
              </div>
            </motion.div>
          )}
          
          {/* Empty state */}
          {chat.messages.length === 0 && !isCurrentlyStreaming && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="flex flex-col items-center justify-center py-8 text-center"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600/20 to-purple-600/20 flex items-center justify-center mb-3">
                <MessageSquare className="h-5 w-5 text-violet-500" />
              </div>
              <p className="text-sm font-medium text-foreground/80 mb-1">Start a conversation</p>
              <p className="text-xs text-muted-foreground/60 max-w-[200px]">
                Ask Partnr anything about your canvas items
              </p>
            </motion.div>
          )}

          {/* Suggested Prompts - show only when waiting for first response or after first response */}
          {!isCurrentlyStreaming && chat.messages.length <= 2 && referencedItems.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs text-muted-foreground">
                {searchEscalationContext ? 'Based on your search:' : 'Suggested prompts:'}
              </p>
              <div className="flex flex-wrap gap-1">
                {(searchEscalationContext ? [
                  `Summarize what I found about "${searchEscalationContext.originalQuery}"`,
                  "Find connections between these",
                  "What's missing from this research?",
                  "Help me expand on this",
                ] : SUGGESTED_PROMPTS).map((prompt, i) => (
                  <motion.button
                    key={prompt}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.05 }}
                    onClick={() => handleSuggestedPrompt(prompt)}
                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-partnr/30 hover:bg-partnr/5 text-muted-foreground hover:text-foreground transition-all cursor-pointer"
                  >
                    {prompt}
                  </motion.button>
                ))}
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t border-border/50 shrink-0">
        <div className="flex items-end gap-2 bg-muted/50 dark:bg-muted/60 rounded-xl px-3 py-2.5 focus-within:ring-1 focus-within:ring-partnr/40 transition-shadow">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              // Auto-resize
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onClick={handleInputClick}
            placeholder="Ask anything..."
            rows={1}
            className="flex-1 bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground/50 resize-none outline-none min-h-[20px] max-h-[120px] leading-relaxed"
            disabled={isCurrentlyStreaming}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isCurrentlyStreaming}
            className={cn(
              "shrink-0 p-1.5 rounded-lg transition-all duration-200 mb-0.5",
              inputValue.trim() && !isCurrentlyStreaming
                ? "bg-partnr text-white hover:opacity-90"
                : "text-muted-foreground/30"
            )}
          >
            {isCurrentlyStreaming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
